'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AuthUser } from '@org/auth-core';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (login: string, password: string, remember?: boolean) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: (force?: boolean) => Promise<boolean>;
  refresh: (force?: boolean) => Promise<boolean>;
}
const Context = createContext<AuthState | null>(null);

export function AuthProvider({ children }: React.PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/check/', { cache: 'no-store' });
      const data = await response.json();
      const current = response.ok && data.authenticated && data.user ? data.user as AuthUser : null;
      setUser(current);
      return !!current;
    } catch {
      setUser(null);
      return false;
    } finally { setIsLoading(false); }
  }, []);

  useEffect(() => {
    // Privileges are always checked with the backend; old localStorage claims
    // from the previous auth provider are never accepted as authentication.
    void checkAuth();
    const interval = window.setInterval(() => { void checkAuth(); }, 5 * 60_000);
    const onFocus = () => { void checkAuth(); };
    window.addEventListener('focus', onFocus);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [checkAuth]);

  const login = useCallback(async (loginValue: string, password: string, remember = true) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/auth/login/', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: loginValue, password, remember }),
      });
      const data = await response.json();
      if (!response.ok || !data.success || !data.user) {
        setError(data.error || 'Не удалось войти. Проверьте логин и пароль.');
        return false;
      }
      setUser(data.user);
      return true;
    } catch {
      setError('Сервер не отвечает. Повторите вход.');
      return false;
    } finally { setIsLoading(false); }
  }, []);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout/', { method: 'POST' }).catch(() => undefined);
    setUser(null);
    window.location.assign('/raschet-zdaniya/');
  }, []);

  const value = useMemo(() => ({ user, isAuthenticated: !!user, isLoading, error,
    login, logout, checkAuth, refresh: checkAuth }), [user, isLoading, error, login, logout, checkAuth]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAuth() {
  const value = useContext(Context);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
export function useAuthStatus() { const { isAuthenticated, isLoading } = useAuth(); return { isAuthenticated, isLoading }; }
export function useCurrentUser() { return useAuth().user; }
